import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import toast from 'react-hot-toast';

import apiClient from '../api/client';
import {
  saveToken,
  getToken,
  saveUser,
  getUser,
  clearAuth as clearStorage,
} from '../utils/storage';

const AuthContext = createContext(null);

const PAWALIBRARY_ROLES = [
  'student',
  'teacher',
  'library_user',
];
const canUsePawaLibrary = (user) =>
  !!user && PAWALIBRARY_ROLES.includes(user.role);

const ACCESS_TOKEN_REFRESH_BUFFER = 5 * 60 * 1000;

const getErrorStatus = (error) =>
  error?.response?.status;

const getResponsePayload = (response) =>
  response?.data?.data || response?.data;

const getUserFromResponse = (response) => {
  const payload = getResponsePayload(response);
  return payload?.user || payload;
};

const tokenExpiresWithin = (
  accessToken,
  milliseconds = 0
) => {
  if (!accessToken) return true;

  try {
    const decoded = jwtDecode(accessToken);

    if (!decoded?.exp) return true;

    return (
      decoded.exp * 1000 <=
      Date.now() + milliseconds
    );
  } catch {
    return true;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth must be used inside AuthProvider'
    );
  }

  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshPromiseRef = useRef(null);

  const setAuthorizationHeader = useCallback(
    (accessToken) => {
      if (accessToken) {
        apiClient.defaults.headers.common.Authorization =
          `Bearer ${accessToken}`;
      } else {
        delete apiClient.defaults.headers.common.Authorization;
      }
    },
    []
  );

  const persistUser = useCallback(async (nextUser) => {
    if (!nextUser) return;

    localStorage.setItem(
      'user',
      JSON.stringify(nextUser)
    );

    await saveUser(nextUser);
    setUserState(nextUser);
  }, []);

  // Keep setUser compatible with pages that already use it.
  const setUser = useCallback(
    (nextUserOrUpdater) => {
      setUserState((currentUser) => {
        const nextUser =
          typeof nextUserOrUpdater === 'function'
            ? nextUserOrUpdater(currentUser)
            : nextUserOrUpdater;

        if (nextUser) {
          localStorage.setItem(
            'user',
            JSON.stringify(nextUser)
          );

          Promise.resolve(saveUser(nextUser)).catch(
            (error) => {
              console.warn(
                'Could not persist updated user:',
                error
              );
            }
          );
        } else {
          localStorage.removeItem('user');
        }

        return nextUser;
      });
    },
    []
  );

  const persistAccessToken = useCallback(
    async (accessToken) => {
      localStorage.setItem('token', accessToken);
      await saveToken(accessToken);
      setAuthorizationHeader(accessToken);
      setToken(accessToken);
    },
    [setAuthorizationHeader]
  );

  const persistSession = useCallback(
    async ({
      accessToken,
      refreshToken,
      userData,
    }) => {
      await persistAccessToken(accessToken);

      if (refreshToken) {
        localStorage.setItem(
          'refreshToken',
          refreshToken
        );
      }

      if (userData) {
        await persistUser(userData);
      }
    },
    [persistAccessToken, persistUser]
  );

  const clearAuth = useCallback(async () => {
    localStorage.removeItem('token');
    // Remove legacy token keys as well. Older Library builds read these keys,
    // so leaving them behind can authenticate a logged-out/guest browser as
    // the previous user and expose that user's purchased-book badges.
    localStorage.removeItem('accessToken');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    try {
      await clearStorage();
    } catch (error) {
      console.warn(
        'Could not clear persisted authentication:',
        error
      );
    } finally {
      refreshPromiseRef.current = null;
      setAuthorizationHeader(null);
      setToken(null);
      setUserState(null);
      setLoading(false);
    }
  }, [setAuthorizationHeader]);

  /*
   * Use the backend's persistent refresh token to obtain a new
   * one-hour access token. If several parts of the app try to
   * refresh together, they all share the same request.
   */
  const refreshSession = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const storedRefreshToken =
      localStorage.getItem('refreshToken');

    if (!storedRefreshToken) {
      const error = new Error(
        'Refresh token missing'
      );
      error.code = 'REFRESH_TOKEN_MISSING';
      throw error;
    }

    refreshPromiseRef.current = (async () => {
      const response = await apiClient.post(
        '/auth/refresh',
        {
          refreshToken: storedRefreshToken,
        }
      );

      const payload = getResponsePayload(response);

      if (!payload?.accessToken) {
        throw new Error(
          'The server did not return a new access token.'
        );
      }

      await persistAccessToken(payload.accessToken);

      if (payload.refreshToken) {
        localStorage.setItem(
          'refreshToken',
          payload.refreshToken
        );
      }

      return payload.accessToken;
    })();

    try {
      return await refreshPromiseRef.current;
    } finally {
      refreshPromiseRef.current = null;
    }
  }, [persistAccessToken]);

  const fetchCurrentUser = useCallback(
    async (accessToken) => {
      setAuthorizationHeader(accessToken);

      const response = await apiClient.get('/users/me');
      const freshUser = getUserFromResponse(response);

      if (!freshUser?.id) {
        throw new Error(
          'The server did not return valid user information.'
        );
      }

      if (!canUsePawaLibrary(freshUser)) {
        const error = new Error(
          'This account cannot use PawaLibrary. Only students, teachers and library users are allowed.'
        );
        error.code = 'ROLE_NOT_ALLOWED';
        throw error;
      }

      await persistUser(freshUser);
      return freshUser;
    },
    [persistUser, setAuthorizationHeader]
  );

  /*
   * Restore the user when the frontend opens. An expired access
   * token no longer logs the user out; it is silently refreshed.
   */
  useEffect(() => {
    let active = true;

    const loadUser = async () => {
      setLoading(true);

      let storedToken = null;

      try {
        storedToken = await getToken();
      } catch (error) {
        console.warn(
          'Could not read the stored token:',
          error
        );
      }

      if (!storedToken) {
        storedToken = localStorage.getItem('token');

        if (storedToken) {
          try {
            await saveToken(storedToken);
          } catch (error) {
            console.warn(
              'Could not migrate the stored token:',
              error
            );
          }
        }
      }

      const storedRefreshToken =
        localStorage.getItem('refreshToken');

      let cachedUser = null;

      try {
        cachedUser = await getUser();
      } catch (error) {
        console.warn(
          'Could not read the stored user:',
          error
        );
      }

      if (!cachedUser) {
        const localUser = localStorage.getItem('user');

        if (localUser) {
          try {
            cachedUser = JSON.parse(localUser);
            await saveUser(cachedUser);
          } catch (error) {
            localStorage.removeItem('user');
          }
        }
      }

      if (cachedUser && active) {
        setUserState(cachedUser);
      }

      if (!storedToken && !storedRefreshToken) {
        if (active) {
          setUserState(null);
          setLoading(false);
        }
        return;
      }

      try {
        // Refresh first if the access token expired while the app
        // was closed or is about to expire.
        if (
          !storedToken ||
          tokenExpiresWithin(storedToken, 30000)
        ) {
          storedToken = await refreshSession();
        } else {
          setAuthorizationHeader(storedToken);
          setToken(storedToken);
        }

        const freshUser = await fetchCurrentUser(
          storedToken
        );

        if (active) {
          setUserState(freshUser);
        }
      } catch (error) {
        const status = getErrorStatus(error);

        if (status === 401) {
          try {
            // The access token may have expired during the profile
            // request. Refresh it and retry once.
            const newAccessToken =
              await refreshSession();

            const freshUser = await fetchCurrentUser(
              newAccessToken
            );

            if (active) {
              setUserState(freshUser);
            }
          } catch (refreshError) {
            if (
              getErrorStatus(refreshError) === 401 ||
              refreshError.code ===
                'REFRESH_TOKEN_MISSING'
            ) {
              await clearAuth();
            }
            // Preserve cached login during temporary network or
            // server failures. The online listener retries later.
          }
        } else if (
          error.code === 'REFRESH_TOKEN_MISSING'
        ) {
          await clearAuth();
        }
        // Do not clear the login for 403, offline errors or a
        // temporary backend failure.
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadUser();

    return () => {
      active = false;
    };
  }, [
    clearAuth,
    fetchCurrentUser,
    refreshSession,
    setAuthorizationHeader,
  ]);

  // Automatically refresh five minutes before access-token expiry.
  useEffect(() => {
    if (!token) return undefined;

    let expiresAt = 0;

    try {
      const decoded = jwtDecode(token);
      expiresAt = (decoded?.exp || 0) * 1000;
    } catch {
      expiresAt = 0;
    }

    const remainingTime = expiresAt - Date.now();

    const refreshAfter = remainingTime <= 0
      ? 0
      : Math.max(
          1000,
          remainingTime -
            ACCESS_TOKEN_REFRESH_BUFFER
        );

    const timer = window.setTimeout(async () => {
      try {
        await refreshSession();
      } catch (error) {
        if (
          getErrorStatus(error) === 401 ||
          error.code === 'REFRESH_TOKEN_MISSING'
        ) {
          toast.error(
            'Your login is no longer valid. Please sign in again.'
          );
          await clearAuth();
        }
        // Temporary offline/server failures keep the cached login.
      }
    }, refreshAfter);

    return () => {
      window.clearTimeout(timer);
    };
  }, [token, refreshSession, clearAuth]);

  /*
   * Browsers pause timers when a device sleeps or a tab stays in
   * the background. Check again whenever the user returns or the
   * internet connection comes back.
   */
  useEffect(() => {
    const maintainSession = async () => {
      if (
        document.visibilityState === 'hidden' ||
        !localStorage.getItem('refreshToken')
      ) {
        return;
      }

      const currentToken =
        localStorage.getItem('token');

      if (
        currentToken &&
        !tokenExpiresWithin(
          currentToken,
          ACCESS_TOKEN_REFRESH_BUFFER
        )
      ) {
        return;
      }

      try {
        await refreshSession();
      } catch (error) {
        if (getErrorStatus(error) === 401) {
          await clearAuth();
        }
        // Stay logged in while offline or during temporary failures.
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        maintainSession();
      }
    };

    window.addEventListener('focus', maintainSession);
    window.addEventListener('online', maintainSession);
    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    return () => {
      window.removeEventListener(
        'focus',
        maintainSession
      );
      window.removeEventListener(
        'online',
        maintainSession
      );
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
    };
  }, [refreshSession, clearAuth]);

  const login = useCallback(
    async (email, password) => {
      try {
        const response = await apiClient.post(
          '/auth/login',
          {
            email: email.trim().toLowerCase(),
            password,
            app: 'pawalibrary',
          }
        );

        const payload = getResponsePayload(response);
        const {
          accessToken,
          refreshToken,
          user: userData,
        } = payload || {};

        if (!accessToken || !userData) {
          toast.error('Invalid server response');
          return { success: false };
        }

        if (!canUsePawaLibrary(userData)) {
          return {
            success: false,
            message: 'This account cannot use PawaLibrary. Only students and library users are allowed.',
          };
        }

        await persistSession({
          accessToken,
          refreshToken,
          userData,
        });

        return { success: true };
      } catch (error) {
        const message =
          error.response?.data?.message ||
          'Login failed';

        toast.error(message);
        return {
          success: false,
          message,
        };
      }
    },
    [persistSession]
  );

  const register = useCallback(
    async (userData) => {
      try {
        const response = await apiClient.post(
          '/auth/register',
          userData
        );

        const payload = getResponsePayload(response);
        const {
          user: registeredUser,
          accessToken,
          refreshToken,
        } = payload || {};

        if (!accessToken || !registeredUser) {
          toast.error('Invalid server response');
          return { success: false };
        }

        await persistSession({
          accessToken,
          refreshToken,
          userData: registeredUser,
        });

        return { success: true };
      } catch (error) {
        const message =
          error.response?.data?.message ||
          'Registration failed';

        toast.error(message);
        return {
          success: false,
          message,
        };
      }
    },
    [persistSession]
  );

  const forgotPassword = useCallback(async (email) => {
    try {
      const response = await apiClient.post(
        '/auth/forgot-password',
        { email }
      );

      const result = response.data;

      if (result.success) {
        const message =
          result.message ||
          'Reset code sent to your email.';

        toast.success(message);
        return { success: true, message };
      }

      const message =
        result.message ||
        'Failed to send reset code.';

      toast.error(message);
      return { success: false, message };
    } catch (error) {
      const message =
        error.response?.data?.message ||
        'An error occurred. Please try again.';

      toast.error(message);
      return { success: false, message };
    }
  }, []);

  const resetPassword = useCallback(
    async (email, code, newPassword) => {
      try {
        const response = await apiClient.post(
          '/auth/reset-password',
          {
            email,
            code,
            newPassword,
          }
        );

        const result = response.data;

        if (result.success) {
          const message =
            result.message ||
            'Password updated successfully.';

          toast.success(message);
          return { success: true, message };
        }

        const message =
          result.message ||
          'Invalid code or expired.';

        toast.error(message);
        return { success: false, message };
      } catch (error) {
        const message =
          error.response?.data?.message ||
          'An error occurred. Please try again.';

        toast.error(message);
        return { success: false, message };
      }
    },
    []
  );

  const logout = useCallback(async () => {
    await clearAuth();
  }, [clearAuth]);

  const value = {
    user,
    setUser,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    refreshSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;